import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
// Personal to-do cell for the scheduler panel (list + mini add form + schedule
// as a Personal block). Extracted from SchedulableItemsPanel.tsx (P2).
import { notify } from "../Toast";
import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { fmtTodoDue } from "./schedulerShared";
export function PersonalTodoCell({ expanded, onToggle, onSchedule, refreshTick, }) {
    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [title, setTitle] = useState("");
    const [desc, setDesc] = useState("");
    const [due, setDue] = useState("");
    const [busy, setBusy] = useState(false);
    const [showDone, setShowDone] = useState(false);
    async function load() {
        try {
            const rows = await api.get("/me/personal-todos");
            setItems(rows);
        }
        catch {
            /* leave prior list; transient */
        }
        finally {
            setLoaded(true);
        }
    }
    // Lazy-load on first expand, and refetch whenever the parent bumps the tick
    // (so a block scheduled from a row keeps the panel in sync).
    useEffect(() => {
        if (expanded && !loaded)
            void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded]);
    useEffect(() => {
        if (expanded && loaded)
            void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTick]);
    async function add(e) {
        e.preventDefault();
        const t = title.trim();
        if (!t || busy)
            return;
        setBusy(true);
        try {
            await api.post("/me/personal-todos", {
                title: t,
                description: desc.trim() || null,
                due_date: due || null,
            });
            setTitle("");
            setDesc("");
            setDue("");
            await load();
        }
        catch (err) {
            notify.error(err instanceof Error ? err.message : "Couldn't add to-do");
        }
        finally {
            setBusy(false);
        }
    }
    async function toggleDone(item) {
        try {
            await api.patch(`/me/personal-todos/${item.id}`, {
                is_done: !item.is_done,
            });
            await load();
        }
        catch (err) {
            notify.error(err instanceof Error ? err.message : "Couldn't update to-do");
        }
    }
    async function remove(item) {
        if (!confirm(`Delete personal to-do "${item.title}"?`))
            return;
        try {
            await api.del(`/me/personal-todos/${item.id}`);
            await load();
        }
        catch (err) {
            notify.error(err instanceof Error ? err.message : "Couldn't delete to-do");
        }
    }
    const active = items.filter((i) => !i.is_done);
    const done = items.filter((i) => i.is_done);
    return (_jsxs("div", { className: `schedulable-widget ${expanded ? "expanded" : "collapsed"}`, children: [_jsxs("button", { type: "button", className: "schedulable-widget-head schedulable-widget-toggle", onClick: onToggle, "aria-expanded": expanded, children: [_jsxs("h4", { children: ["Personal", " ", active.length > 0 && (_jsxs("span", { className: "muted", style: { fontWeight: 400 }, children: ["\u00B7 ", active.length] }))] }), _jsx("span", { className: "widget-toggle-icon", "aria-hidden": "true", children: expanded ? "−" : "+" })] }), expanded && (_jsxs(_Fragment, { children: [_jsx("p", { className: "muted small scheduler-claim-hint", children: "Your private to-do list. Click an item to block time for it as a Personal block (only you see the title). Check it off or delete it when you're done." }), _jsxs("form", { className: "personal-todo-add", onSubmit: add, children: [_jsx("input", { type: "text", className: "personal-todo-input", placeholder: "Add a personal to-do\u2026", value: title, onChange: (e) => setTitle(e.target.value), maxLength: 300 }), _jsx("input", { type: "text", className: "personal-todo-input", placeholder: "Description (optional)", value: desc, onChange: (e) => setDesc(e.target.value) }), _jsxs("div", { className: "personal-todo-add-row", children: [_jsx("input", { type: "date", className: "personal-todo-date", value: due, onChange: (e) => setDue(e.target.value), title: "Optional due date" }), _jsx("button", { type: "submit", className: "btn-primary btn-small", disabled: busy || !title.trim(), children: busy ? "…" : "Add" })] })] }), loaded && active.length === 0 && (_jsx("div", { className: "muted small", children: "Nothing on your personal list." })), active.length > 0 && (_jsx("ul", { className: "schedulable-list", children: active.map((item) => {
                            const dueLabel = fmtTodoDue(item.due_date);
                            return (_jsxs("li", { className: "schedulable-item personal-todo-row", onClick: () => onSchedule(item), title: "Click to block time for this as a Personal block", children: [_jsx("div", { className: "item-title", children: item.title }), item.description && (_jsx("div", { className: "item-meta", children: _jsx("span", { className: "muted", children: item.description }) })), dueLabel && (_jsxs("div", { className: "item-when muted", children: ["Due ", dueLabel] })), _jsxs("div", { className: "item-actions", children: [_jsx("button", { type: "button", className: "btn-primary btn-small", onClick: (e) => {
                                                    e.stopPropagation();
                                                    onSchedule(item);
                                                }, title: "Block time for this on your calendar as a Personal block", children: "+ Calendar" }), _jsx("button", { type: "button", className: "btn-secondary btn-small", onClick: (e) => {
                                                    e.stopPropagation();
                                                    void toggleDone(item);
                                                }, title: "Mark this to-do done", children: "\u2713 Done" }), _jsx("button", { type: "button", className: "btn-danger btn-small", onClick: (e) => {
                                                    e.stopPropagation();
                                                    void remove(item);
                                                }, title: "Delete this to-do", children: "Delete" })] })] }, item.id));
                        }) })), done.length > 0 && (_jsxs("div", { className: "personal-todo-done", children: [_jsxs("button", { type: "button", className: "pm-oneoff-completed-head", onClick: () => setShowDone((v) => !v), "aria-expanded": showDone, children: [_jsxs("span", { children: ["\u2713 Done \u00B7 ", done.length] }), _jsx("span", { "aria-hidden": "true", children: showDone ? "▾" : "▸" })] }), showDone && (_jsx("ul", { className: "schedulable-list", children: done.map((item) => (_jsxs("li", { className: "schedulable-item personal-todo-row done", children: [_jsx("div", { className: "item-title", children: item.title }), _jsxs("div", { className: "item-actions", children: [_jsx("button", { type: "button", className: "btn-secondary btn-small", onClick: () => void toggleDone(item), title: "Move back to active", children: "Undo" }), _jsx("button", { type: "button", className: "btn-danger btn-small", onClick: () => void remove(item), title: "Delete this to-do", children: "Delete" })] })] }, item.id))) }))] }))] }))] }));
}
